import { parsePhoneNumberFromString } from 'libphonenumber-js';

// US/Hawaii numbers (the common case) render national; +prefixed foreign
// numbers render international; unparseable input falls back to raw.
export function formatPhone(phone) {
    const parsed = parsePhoneNumberFromString(phone, 'US');
    if (!parsed) return phone;
    return parsed.country === 'US' ? parsed.formatNational() : parsed.formatInternational();
}

// Group seat names like "T12-10" by their table prefix (text before the last
// dash) -> [{ table: 'T12', seats: ['10', ...] }], preserving first-seen order.
export function groupSeatsByTable(seatNames) {
    const groups = {};
    const order = [];
    for (const name of seatNames) {
        const dash = name.lastIndexOf('-');
        const table = dash > 0 ? name.slice(0, dash) : '';
        const seat = dash > 0 ? name.slice(dash + 1) : name;
        if (!groups[table]) {
            groups[table] = [];
            order.push(table);
        }
        groups[table].push(seat);
    }
    return order.map((table) => ({ table, seats: groups[table] }));
}

function accumulate(target, ticketTypes, keyOf) {
    if (!Array.isArray(ticketTypes)) return target;
    for (const tt of ticketTypes) {
        for (const opt of tt.options ?? []) {
            const key = keyOf(opt);
            if (key) target[key] = (target[key] || 0) + (opt.count || 0);
        }
    }
    return target;
}

export function getPrimaryTicketTypeName(ticketTypes) {
    if (!Array.isArray(ticketTypes) || ticketTypes.length === 0) return '—';
    return ticketTypes.reduce((max, tt) => (tt.count > max.count ? tt : max)).name;
}

// Mixed = booking has tickets from more than one type. Only its primary type
// lands it in a group, so any other non-zero type is foreign to that group.
export function isMixedBooking(ticketTypes) {
    if (!Array.isArray(ticketTypes)) return false;
    let nonZero = 0;
    for (const tt of ticketTypes) {
        if ((tt.count || 0) > 0) {
            nonZero += 1;
            if (nonZero > 1) return true;
        }
    }
    return false;
}

// Entries to display in the "ticket count" cell. Prefers age bands when the
// view groups by age band, falls back to option names, then raw type counts.
export function getTicketCountEntries(ticketTypes, { groupByAgeBand } = {}) {
    if (!Array.isArray(ticketTypes) || ticketTypes.length === 0) return [];
    const plainEntries = ticketTypes
        .filter((tt) => !(tt.options?.length > 0))
        .map((tt) => [tt.name, tt.count]);
    if (groupByAgeBand) {
        const bands = accumulate({}, ticketTypes, (o) => o.ageBand);
        if (Object.keys(bands).length > 0) return [...Object.entries(bands), ...plainEntries];
    }
    const opts = accumulate({}, ticketTypes, (o) => o.name);
    if (Object.keys(opts).length > 0) return [...Object.entries(opts), ...plainEntries];
    return ticketTypes.map((tt) => [tt.name, tt.count]);
}

export function computeGroupStats(bookings, { groupByAgeBand } = {}) {
    let checkedIn = 0;
    let totalTickets = 0;
    const optionCounts = {};
    const bandCounts = {};
    for (const row of bookings) {
        if (row.isCheckedIn) checkedIn += 1;
        if (Array.isArray(row.ticketTypes)) {
            for (const tt of row.ticketTypes) totalTickets += tt.count || 0;
            accumulate(optionCounts, row.ticketTypes, (o) => o.name);
            accumulate(bandCounts, row.ticketTypes, (o) => o.ageBand);
        }
    }
    // Mirror per-row summary: fall back to option counts when grouping by age
    // band but no bands are configured, so footer matches the rows above.
    const source = groupByAgeBand && Object.keys(bandCounts).length > 0 ? bandCounts : optionCounts;
    return { checkedIn, totalTickets, summaryEntries: Object.entries(source) };
}

export const COL_COUNT = 8;
